'use client';
// src/pages/admin/PlatformClaimsAdmin.jsx
//
// Founder-only admin page for granting / revoking the three platform
// JWT custom claims: founder, support, finance.
//
// Replaces the old "edit users/{uid}.role = 'admin'" Firestore-field
// approach (which had a self-promotion bug). Both the lookup and the
// grant calls go through founder-gated Cloud Functions:
//   - lookupUserForAdmin   (modules/platformAdminLookup.js)
//   - grantPlatformClaim   (modules/platformClaims.js)
//
// Note on token freshness: Firebase Auth's JWT cache means the target
// user won't see the new claim until they sign out + back in OR call
// getIdToken(true). That's a Firebase quirk, not something we can fix
// from this UI — we just surface it as a hint after each grant.

import React, { useState } from 'react';
import { app } from '@/firebase/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';
import {
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  UserCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

const CLAIM_DEFS = [
  {
    key: 'founder',
    label: 'Founder',
    description: 'Full platform admin. Can grant/revoke claims, run backfills, see everything.',
    danger: true,
  },
  {
    key: 'support',
    label: 'Support',
    description: 'Read-only access to user accounts + ability to escalate issues. Cannot modify claims.',
    danger: false,
  },
  {
    key: 'finance',
    label: 'Finance',
    description: 'Access to billing, refunds, and financial reports across all clubs.',
    danger: false,
  },
];

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function PlatformClaimsAdmin() {
  const [email, setEmail]         = useState('');
  const [target, setTarget]       = useState(null);
  const [loading, setLoading]     = useState(false);
  const [updatingKey, setUpdatingKey] = useState(null);

  const handleLookup = async (e) => {
    e?.preventDefault?.();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    setLoading(true);
    setTarget(null);
    const tid = toast.loading('Looking up user…');
    try {
      const fn = httpsCallable(getFunctions(app, 'us-central1'), 'lookupUserForAdmin');
      const { data } = await fn({ email: trimmed });
      setTarget(data);
      toast.success(`Found: ${data.displayName || data.email}`, { id: tid });
    } catch (err) {
      const msg = err?.message || 'Lookup failed';
      toast.error(msg, { id: tid });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (claim, nextValue) => {
    if (!target?.uid) return;
    setUpdatingKey(claim);
    const tid = toast.loading(`${nextValue ? 'Granting' : 'Revoking'} ${claim}…`);
    try {
      const fn = httpsCallable(getFunctions(app, 'us-central1'), 'grantPlatformClaim');
      const { data } = await fn({ uid: target.uid, claim, value: nextValue });
      setTarget((prev) => prev ? { ...prev, claims: data.claims || prev.claims } : prev);
      toast.success(`${claim} ${nextValue ? 'granted' : 'revoked'}`, { id: tid });
    } catch (err) {
      toast.error(err?.message || `Failed to update ${claim}`, { id: tid });
    } finally {
      setUpdatingKey(null);
    }
  };

  const isSelf = target && (target.email || '').toLowerCase()
    === (typeof window !== 'undefined' && window.__SCRIMLY_CURRENT_EMAIL__ || '').toLowerCase();

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-900 p-6 sm:p-8">
      <div className="mx-auto max-w-2xl space-y-6">

        {/* Header */}
        <header>
          <p
            className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500"
            style={{ fontFamily: HEAD_FONT }}
          >
            Admin
          </p>
          <h1
            className="mt-1 text-2xl font-bold uppercase tracking-tight text-zinc-900 dark:text-white"
            style={{ fontFamily: HEAD_FONT }}
          >
            Platform Claims
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            Grant or revoke <code className="rounded bg-zinc-200 dark:bg-zinc-800 px-1 py-0.5 text-[12px]">founder</code>,{' '}
            <code className="rounded bg-zinc-200 dark:bg-zinc-800 px-1 py-0.5 text-[12px]">support</code>, or{' '}
            <code className="rounded bg-zinc-200 dark:bg-zinc-800 px-1 py-0.5 text-[12px]">finance</code> claims.
            Founders only.
          </p>
        </header>

        {/* Lookup form */}
        <form onSubmit={handleLookup} className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-5 shadow-sm">
          <label className="mb-2 block text-xs font-bold uppercase tracking-[0.12em] text-zinc-700 dark:text-zinc-300"
                 style={{ fontFamily: HEAD_FONT }}>
            User email
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="someone@example.com"
                autoComplete="off"
                disabled={loading}
                className="w-full rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 pl-9 pr-4 py-2 text-sm text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-400"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !email.trim()}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-lime-400 hover:bg-lime-500 text-zinc-900 px-4 py-2 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  Searching…
                </>
              ) : 'Look up'}
            </button>
          </div>
        </form>

        {/* Target user card */}
        {target && (
          <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 shadow-sm overflow-hidden">
            {/* Identity */}
            <div className="flex items-center gap-4 p-5 border-b border-zinc-100 dark:border-zinc-700">
              {target.photoURL ? (
                <img src={target.photoURL} alt="" className="h-12 w-12 rounded-full ring-1 ring-zinc-200 dark:ring-zinc-700" />
              ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-zinc-100 dark:bg-zinc-700">
                  <UserCircleIcon className="h-7 w-7 text-zinc-400" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-base font-semibold text-zinc-900 dark:text-white truncate">
                  {target.displayName || '(no display name)'}
                </p>
                <p className="text-xs text-zinc-500 truncate">{target.email}</p>
                <p className="text-[11px] text-zinc-400 mt-0.5 font-mono truncate">{target.uid}</p>
              </div>
              {target.disabled && (
                <span className="inline-flex items-center gap-1 rounded-full bg-red-50 dark:bg-red-900/30 px-2 py-0.5 text-[11px] font-semibold text-red-700 dark:text-red-300">
                  <XCircleIcon className="h-3 w-3" /> Disabled
                </span>
              )}
            </div>

            {/* Account meta */}
            <div className="grid grid-cols-2 gap-3 px-5 py-3 border-b border-zinc-100 dark:border-zinc-700 text-xs">
              <div>
                <span className="block text-zinc-400 uppercase tracking-wider text-[10px] font-semibold mb-0.5">Created</span>
                <span className="text-zinc-700 dark:text-zinc-300">{formatDate(target.creationTime)}</span>
              </div>
              <div>
                <span className="block text-zinc-400 uppercase tracking-wider text-[10px] font-semibold mb-0.5">Last sign-in</span>
                <span className="text-zinc-700 dark:text-zinc-300">{formatDate(target.lastSignInTime)}</span>
              </div>
            </div>

            {/* Self-edit warning */}
            {isSelf && (
              <div className="mx-5 my-3 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2">
                <ExclamationTriangleIcon className="h-4 w-4 shrink-0 mt-0.5" />
                <span>
                  You're editing <strong>your own</strong> account. Revoking the
                  founder claim from yourself will lock you out of this page —
                  you'd have to re-bootstrap via the local script.
                </span>
              </div>
            )}

            {/* Claim toggles */}
            <div className="p-5 space-y-3">
              <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500 mb-2"
                 style={{ fontFamily: HEAD_FONT }}>
                <KeyIcon className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
                Platform claims
              </p>
              {CLAIM_DEFS.map((c) => {
                const active = target.claims?.[c.key] === true;
                const isUpdating = updatingKey === c.key;
                return (
                  <div
                    key={c.key}
                    className={clsx(
                      'flex items-start gap-3 rounded-xl border p-3 transition-colors',
                      active
                        ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20'
                        : 'border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-900',
                    )}
                  >
                    <div className={clsx(
                      'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1',
                      active
                        ? 'bg-emerald-100 dark:bg-emerald-900/40 ring-emerald-300'
                        : 'bg-zinc-100 dark:bg-zinc-800 ring-zinc-200 dark:ring-zinc-700',
                    )}>
                      {active
                        ? <CheckCircleIcon className="h-5 w-5 text-emerald-700 dark:text-emerald-400" />
                        : <ShieldCheckIcon className="h-5 w-5 text-zinc-400" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-zinc-900 dark:text-white">{c.label}</span>
                        {c.danger && (
                          <span className="text-[10px] font-bold uppercase tracking-wide text-red-600 dark:text-red-400">
                            Danger
                          </span>
                        )}
                        {active && (
                          <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
                            Active
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{c.description}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleToggle(c.key, !active)}
                      disabled={isUpdating}
                      className={clsx(
                        'shrink-0 inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                        active
                          ? 'bg-red-600 hover:bg-red-700 text-white'
                          : c.danger
                            ? 'bg-red-600 hover:bg-red-700 text-white'
                            : 'bg-lime-400 hover:bg-lime-500 text-zinc-900',
                      )}
                    >
                      {isUpdating ? (
                        <ArrowPathIcon className="h-4 w-4 animate-spin" />
                      ) : active ? 'Revoke' : 'Grant'}
                    </button>
                  </div>
                );
              })}

              {/* Token refresh hint */}
              <p className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
                Heads up: changes don't take effect in the target user's session
                until they sign out + back in (or their app calls{' '}
                <code className="rounded bg-zinc-200 dark:bg-zinc-700 px-1 text-[10px]">getIdToken(true)</code>).
                Firebase Auth caches the JWT.
              </p>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
